import {
  ArrowDown as ArrowDownIcon,
  Check as CheckIcon,
  Search as SearchIcon,
  Close as CloseIcon,
} from "@/components/icons";
import { DropdownProps } from "@/types/component";

import "./index.scss";

import { t } from "i18next";
import React, { useRef, useState, useEffect, KeyboardEvent } from "react";
import { createPortal } from "react-dom";

const Dropdown: React.FC<DropdownProps> = ({
  allowClear = false,
  disabled = false,
  error = false,
  errorMessage,
  hasMore,
  initialDisplayCount = 10,
  isLoadingMore = false,
  label,
  labelIcon,
  icon,
  multiple = false,
  onScrollEnd,
  onSearch,
  onSelect,
  options,
  placeholder = t("select"),
  searchable = false,
  scrollThreshold = 10,
  selectedLabel,
  menuPosition = "bottom",
  value = null,
  writable = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<string[]>(
    multiple && Array.isArray(value) ? value : [],
  );
  const [selectedOption, setSelectedOption] = useState<null | string>(
    multiple ? null : typeof value === "string" ? value : null,
  );
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const scrollListRef = useRef<HTMLUListElement>(null);
  const [menuRect, setMenuRect] = useState<{
    top: number;
    left: number;
    width: number;
    placement: "top" | "bottom";
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const isScrollingRef = useRef(false);

  // Auto-manage infinity scroll state if onScrollEnd is provided
  const [internalIsLoadingMore, setInternalIsLoadingMore] = useState(false);
  const [internalHasMore, setInternalHasMore] = useState<boolean | undefined>(
    undefined,
  );
  const previousOptionsLengthRef = useRef(options.length);

  // Virtual scrolling: track how many options to display
  const [displayedOptionsCount, setDisplayedOptionsCount] =
    useState(initialDisplayCount);
  const previousFilteredLengthRef = useRef(0);

  // Use internal state if not provided via props
  const isLoadingMoreState =
    isLoadingMore !== undefined ? isLoadingMore : internalIsLoadingMore;
  const hasMoreState =
    hasMore !== undefined
      ? hasMore
      : internalHasMore !== undefined
        ? internalHasMore
        : true;

  useEffect(() => {
    if (disabled || options !== undefined) {
      setIsLoading(false);
    }
  }, [options, disabled]);

  // Auto-detect if more data is available based on options length change
  useEffect(() => {
    if (onScrollEnd && previousOptionsLengthRef.current !== options.length) {
      // If options increased, assume there might be more data
      // If options didn't increase after loading, we might have reached the end
      // This is a simple heuristic - can be overridden with explicit hasMore prop
      if (options.length > previousOptionsLengthRef.current) {
        setInternalHasMore(true);
      }
      previousOptionsLengthRef.current = options.length;
    }
  }, [options.length, onScrollEnd]);

  useEffect(() => {
    if (multiple) {
      setSelectedOptions(Array.isArray(value) ? value : []);
    } else {
      setSelectedOption(typeof value === "string" ? value : null);
    }
  }, [value, multiple]);

  const handleSelect = (option: string) => {
    if (multiple) {
      const updatedSelection = selectedOptions.includes(option)
        ? selectedOptions.filter((item) => item !== option)
        : [...selectedOptions, option];
      setSelectedOptions(updatedSelection);
      if (onSelect) {
        onSelect(updatedSelection);
      }
    } else {
      setSelectedOption(option);
      setIsOpen(false);
      if (onSelect) {
        onSelect(option);
      }
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!writable || !multiple) return;

    if (e.key === "Enter" && searchTerm.trim()) {
      e.preventDefault();

      // Create a new option from the search term
      const newOption = searchTerm.trim();

      // Only add if it's not already in the selection
      if (!selectedOptions.includes(newOption)) {
        const updatedSelection = [...selectedOptions, newOption];
        setSelectedOptions(updatedSelection);
        if (onSelect) {
          onSelect(updatedSelection);
        }
      }

      setSearchTerm("");
    }
  };

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (multiple) {
      setSelectedOptions([]);
      if (onSelect) {
        onSelect([]);
      }
    } else {
      setSelectedOption(null);
      if (onSelect) {
        onSelect("");
      }
    }
  };

  const handleRemoveTag = (event: React.MouseEvent, tagToRemove: string) => {
    event.stopPropagation();
    const updatedSelection = selectedOptions.filter(
      (tag) => tag !== tagToRemove,
    );
    setSelectedOptions(updatedSelection);
    if (onSelect) {
      onSelect(updatedSelection);
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as Node;
    const insideTrigger =
      dropdownRef.current && dropdownRef.current.contains(target);
    const insideMenu =
      scrollListRef.current && scrollListRef.current.contains(target);
    if (!insideTrigger && !insideMenu) {
      setIsOpen(false);
      // Add new tag if there's text in the search input when clicking outside
      if (writable && multiple && searchTerm.trim()) {
        const newOption = searchTerm.trim();
        if (!selectedOptions.includes(newOption)) {
          const updatedSelection = [...selectedOptions, newOption];
          setSelectedOptions(updatedSelection);
          if (onSelect) {
            onSelect(updatedSelection);
          }
        }
      }
      // Clear search term when dropdown closes
      if (searchTerm !== "") {
        setSearchTerm("");
        if (onSearch) onSearch("");
      }
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [writable, multiple, searchTerm, selectedOptions]);

  // Menu pozisyonunu hesapla (portal için)
  useEffect(() => {
    if (!isOpen) {
      setMenuRect(null);
      return;
    }
    const updateRect = () => {
      const el = dropdownRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const menuMaxHeight = 260;
      const spaceBelow = window.innerHeight - r.bottom;
      const spaceAbove = r.top;
      const preferTop = menuPosition === "top";
      const useTop = preferTop
        ? spaceAbove >= menuMaxHeight || spaceAbove > spaceBelow
        : spaceBelow < menuMaxHeight && spaceAbove > spaceBelow;
      setMenuRect({
        top: useTop ? r.top : r.bottom,
        left: r.left,
        width: r.width,
        placement: useTop ? "top" : "bottom",
      });
    };
    updateRect();
    // Menü portal ile sabit konumda çizildiği için sayfa kaydırılınca tetikleyiciden
    // kopup başka alanların (tarih seçici vb.) üstünde kalıyor, tıklamaları
    // yutuyordu. Liste içi kaydırma dışında her kaydırmada menü kapanır.
    const handleScroll = (event: Event) => {
      const target = event.target as Node | null;
      if (target && scrollListRef.current?.contains(target)) return;
      setIsOpen(false);
    };
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", updateRect);
    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", updateRect);
    };
  }, [isOpen, menuPosition]);

  // Add effect to clear search term when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      if (searchTerm !== "") {
        setSearchTerm("");
        if (onSearch) onSearch("");
      }
      // Reset displayed count when dropdown closes (only for static options)
      if (!onScrollEnd) {
        setDisplayedOptionsCount(initialDisplayCount);
      }
    } else {
      // Reset displayed count when dropdown opens (only for static options)
      // For dynamic options, start with initialDisplayCount but update as data comes
      if (!onScrollEnd) {
        setDisplayedOptionsCount(initialDisplayCount);
      } else {
        // For dynamic options, show all available options from the start
        // But if options.length is less than initialDisplayCount, use that
        setDisplayedOptionsCount(Math.max(initialDisplayCount, options.length));
      }
    }
  }, [
    isOpen,
    initialDisplayCount,
    onScrollEnd,
    options.length,
    onSearch,
    searchTerm,
  ]);

  const filteredOptions = options.filter((option) => {
    if (
      !option.label.toLowerCase().includes(searchTerm.toLowerCase())
    ) {
      return false;
    }
    // Multi-select modunda zaten seçilmiş item'lar listede tekrar
    // gözükmesin — chip olarak ayrıca render ediliyor.
    if (multiple && selectedOptions.includes(option.value)) {
      return false;
    }
    return true;
  });

  // Reset displayed count when search term changes or filtered options change significantly
  // For static options: reset to initialDisplayCount
  // For dynamic options: update to match options length when it increases
  useEffect(() => {
    if (filteredOptions.length !== previousFilteredLengthRef.current) {
      if (!onScrollEnd) {
        // Static options: reset to initial count
        setDisplayedOptionsCount(initialDisplayCount);
      } else {
        // Dynamic options: update to show all available options
        // Use options.length directly to ensure we get the latest value
        setDisplayedOptionsCount((prev) => Math.max(prev, options.length));
      }
      previousFilteredLengthRef.current = filteredOptions.length;
    }
  }, [
    filteredOptions.length,
    options.length,
    initialDisplayCount,
    onScrollEnd,
  ]);

  // Slice options to show only displayed count
  // For static options: displayedOptionsCount is controlled by scroll
  // For dynamic options: displayedOptionsCount is updated automatically when options.length increases
  const displayedOptions = filteredOptions.slice(0, displayedOptionsCount);
  const hasMoreStaticOptions =
    !onScrollEnd && filteredOptions.length > displayedOptionsCount;

  const handleScroll = (event: React.UIEvent<HTMLUListElement>) => {
    const { clientHeight, scrollHeight, scrollTop } = event.currentTarget;

    // Calculate distance from bottom
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

    // Check if we should load more (either from static options or dynamic)
    const shouldLoadStatic =
      !onScrollEnd &&
      hasMoreStaticOptions &&
      distanceFromBottom <= scrollThreshold;
    const shouldLoadDynamic =
      onScrollEnd &&
      (hasMoreState === true || hasMoreState === undefined) &&
      distanceFromBottom <= scrollThreshold;

    // Don't trigger if already loading or currently processing a scroll
    if (isLoadingMoreState || isScrollingRef.current) {
      return;
    }

    // Handle static options loading (no onScrollEnd callback)
    if (shouldLoadStatic) {
      isScrollingRef.current = true;
      // Load next chunk of static options with smooth animation
      // Add delay to create loading effect
      setTimeout(() => {
        setDisplayedOptionsCount((prev) =>
          Math.min(prev + initialDisplayCount, filteredOptions.length),
        );
        isScrollingRef.current = false;
      }, 600);
      return;
    }

    // Handle dynamic options loading (with onScrollEnd callback)
    if (shouldLoadDynamic) {
      isScrollingRef.current = true;

      // Auto-manage loading state if not provided via props
      if (isLoadingMore === undefined) {
        setInternalIsLoadingMore(true);
      }

      // Call onScrollEnd and handle promise/return value
      const result = onScrollEnd();

      // If onScrollEnd returns a promise, wait for it
      if (
        result &&
        typeof result === "object" &&
        "then" in result &&
        typeof (result as Promise<any>).then === "function"
      ) {
        result
          .then((hasMore: boolean | undefined) => {
            if (hasMore !== undefined) {
              setInternalHasMore(hasMore);
            }
            // Update displayed count when new data arrives for dynamic options
            // Options prop will be updated by parent component, useEffect will handle the update
            if (isLoadingMore === undefined) {
              setInternalIsLoadingMore(false);
            }
            isScrollingRef.current = false;
          })
          .catch(() => {
            setInternalHasMore(false);
            if (isLoadingMore === undefined) {
              setInternalIsLoadingMore(false);
            }
            isScrollingRef.current = false;
          });
      } else {
        // If onScrollEnd returns a boolean, use it for hasMore
        if (typeof result === "boolean") {
          setInternalHasMore(result);
        }

        // Update displayed count when new data arrives for dynamic options
        // Options prop will be updated by parent component, useEffect will handle the update

        // Reset flag after a short delay to allow for async operations
        setTimeout(() => {
          if (isLoadingMore === undefined) {
            setInternalIsLoadingMore(false);
          }
          isScrollingRef.current = false;
        }, 500);
      }
    }
  };

  useEffect(() => {
    // Focus search input when dropdown opens
    if (isOpen && searchInputRef.current && (searchable || writable)) {
      searchInputRef.current.focus();
    }
  }, [isOpen, searchable, writable]);

  const renderSelectedTags = () => {
    if (!multiple || selectedOptions.length === 0) return null;

    return (
      <div className="selected-tags">
        {selectedOptions.map((opt, index) => {
          const foundOption = options.find((o) => o.value === opt);
          const displayText = foundOption ? foundOption.label : opt;
          return (
            <span className="tag" key={index}>
              {displayText}
              <span
                className="tag-delete"
                onClick={(e) => handleRemoveTag(e, opt)}
              >
                <CloseIcon width={16} height={16} color="#707A8F" />
              </span>
            </span>
          );
        })}
      </div>
    );
  };

  return (
    <div
      className={`dropdown-container ${error ? "error" : ""} ${disabled ? "disabled" : ""}`}
      ref={dropdownRef}
    >
      {label && (
        <label className={`dropdown-label ${error ? "error" : ""}`}>
          {labelIcon && (
            <span className="dropdown-label-icon">{labelIcon}</span>
          )}
          {label}
        </label>
      )}
      <div
        className={`dropdown-header ${isOpen ? "focused" : ""} ${selectedLabel && selectedLabel !== placeholder ? "has-value" : ""}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        {icon && <span className="dropdown-header-icon">{icon}</span>}
        <span className="dropdown-selected">
          {isLoading ? (
            <span className="loading-indicator">{t("common.loading")}</span>
          ) : disabled ? (
            multiple ? (
              selectedOptions.length > 0 ? (
                renderSelectedTags()
              ) : (
                placeholder
              )
            ) : (
              selectedLabel || placeholder
            )
          ) : !isLoading && options.length === 0 && !writable ? (
            t("no_data")
          ) : multiple ? (
            selectedOptions.length > 0 ? (
              renderSelectedTags()
            ) : (
              placeholder
            )
          ) : (
            selectedLabel || placeholder
          )}
        </span>
        {allowClear &&
          (selectedOption || (multiple && selectedOptions.length > 0)) && (
            <button
              aria-label="Clear selected option"
              className="dropdown-clear"
              onClick={handleClear}
            >
              ✕
            </button>
          )}
        <span className={`dropdown-icon ${isOpen ? "open" : ""}`}>
          <ArrowDownIcon height={18} width={18} />
        </span>
      </div>
      {isOpen &&
        menuRect &&
        createPortal(
          <ul
            className={`dropdown-list dropdown-list-portal ${menuRect.placement === "top" ? "position-top" : ""}`}
            onScroll={handleScroll}
            ref={scrollListRef}
            style={{
              position: "fixed",
              top:
                menuRect.placement === "top"
                  ? undefined
                  : `${menuRect.top}px`,
              bottom:
                menuRect.placement === "top"
                  ? `${window.innerHeight - menuRect.top}px`
                  : undefined,
              left: `${menuRect.left}px`,
              width: `${menuRect.width}px`,
            }}
          >
            {(searchable || writable) && (
              <div className="dropdown-search-wrapper">
                <input
                  className="dropdown-search"
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    if (onSearch) onSearch(e.target.value);
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder={
                    writable
                      ? t("buttons.add_new_option")
                      : t("buttons.searchPlaceholder")
                  }
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                />
                <span className="dropdown-search-icon">
                  <SearchIcon width={20} height={20} />
                </span>
              </div>
            )}
            {displayedOptions.map((option, index) => {
              const isSelected = multiple
                ? selectedOptions.includes(option.value)
                : selectedOption === option.value;
              return (
                <li
                  className={`dropdown-option ${isSelected ? "selected" : ""}`}
                  key={`${option.value}-${index}`}
                  onClick={() => handleSelect(option.value)}
                >
                  {option.label}
                  {isSelected && (
                    <span className="checkmark">
                      <CheckIcon
                        width={20}
                        height={20}
                        color="var(--color-primary, #e7212b)"
                      />
                    </span>
                  )}
                </li>
              );
            })}
            {isLoadingMoreState && (
              <li className="dropdown-loading">
                <span className="loading-indicator">{t("common.loading")}</span>
              </li>
            )}
          </ul>,
          document.body,
        )}
      {error && errorMessage && (
        <span className="dropdown-error">{errorMessage}</span>
      )}
    </div>
  );
};

export default Dropdown;
